import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';

import { authUserState } from './common';
import { checkRights } from '../functions';
import { ProductTypeTitles } from '../dictionary';

export class RequestState {
	constructor() {
		this.IsSendRequest = false;
		this.Requests = [];
		this.Request = undefined;

		this.Providers = [];
		this.Statuses = { 1 : "Новая", 2 : "Обработана" };
	}

	/** Флаги прав работы с заявками */
	get CanManage () { return authUserState ? authUserState.Shift.Active : false; }
	get CanProcess () { return authUserState ? checkRights("ManagePurchase") : false; }

	/** Имя пользователя */
	get ShiftUser () { return authUserState ? authUserState.User.Name : "" }

	/** Список доступных складов */
	get Stocks () { return authUserState ? authUserState.Stocks : []; }

	/** Формирование столбцов таблицы */
	get columns () {
		return {
			Date: { filterable: false, Header: "Дата создания" }, 
			User: {}, 
			Stock: {},
			Status : { filterable: false, accessor : "StatusName" },
			ProductType : { filterable: false, accessor : "ProductType", Header: "Тип" },
			View: {},
			...(this.CanProcess ? { Process : {}} : {}) ,
			Remove : {}
		};
	}

	/**
	 * Разбор заявок в таблицу
	 * @param {object} data ответ от сервера со списком заявок
	 */
	setRequests(data) {
		this.Requests = !data.Success ? [] : _.orderBy(_.map(data.Requests, (v) => {
			return _.extend(v, {
				Stock: v.StockName,
				StatusName : this.Statuses[v.Status],
				ProductType : _.find(ProductTypeTitles, { Id : v.Type }).Name,
				CanRemove: v.Status === 1 && ((this.CanProcess || v.User === this.ShiftUser)),
				CanProcess: v.Status === 1 && v.Type === 1 && this.CanProcess
			})
		}), ['Status', 'Date'], ['asc', 'desc']);

		this.IsSendRequest = true;
	}

	/**
	 * Разбор выбранной заявки
	 * @param {object} request ответ от сервера со заявкой
	 */
	setRequest(request, type = "") {
		this.Request = !_.isUndefined(request) ? new Request(request, type) : undefined;

		if (this.Request && this.Request.CreateMode && this.Stocks.length === 1)
			this.Request.StockId = this.Stocks[0].Id;
	}

	/**
	 * Фильтрация поставщик по продукту
	 * @param {string} product Название продукта
	 * @returns {[object]} список поставщиков
	 */
	getProvidersByProduct = (product) => {
		return _.map(_.filter(this.Providers, (item) => { 
			return _.indexOf(item.Products, product) !== -1; }), (item) => {
				return { Id : item.Id, Name : item.Name }
			});
	}
}
decorate(RequestState, {
	Requests: observable,
	IsSendRequest: observable,
	Request: observable,
	
	Providers: observable,
	Stocks: computed,
	
	CanManage: computed,
	CanProcess: computed
});

export class Request {
	constructor(item, type) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Date = item && item.Date ? item.Date : ""; 
		this.Status = item && item.Status ? item.Status : 1;
		this.User = item && item.User ? item.User : "";
		this.StockId = item && item.StockId ? item.StockId : -1;
		this.StockName = item && item.StockName ? item.StockName : "";
		this.RawIds = item && item.RawIds ? item.RawIds : []
		
		this.TemplateType = item && item.TemplateType ? item.TemplateType : -1;
		this.OperationType = type;
		
		this.Products = new RequestProducts(item.Products, this.OperationType)
		
		this.ErrorCode = "";
		this.ButtonLoading = false;
	}

	get Templates () { return [{ Id: 1, Name: "По всему складу" }, { Id: 2, Name: "Выборочно" }]; }

	get CreateMode () { return this.OperationType === "new"; }
	get ProcessMode () { return this.OperationType === "process"; }
	get ViewMode () { return this.OperationType === "view"; }
	get TemplateMode () { return this.CreateMode && this.StockId > 0 && this.TemplateType && _.isEmpty(this.Products.List) }

	get PageTitle () { return this.ProcessMode ? "Обработка заявки" : this.Id === -1 ? "Новая заявка" : "Просмотр заявки"; }

	get CanManage () {
		return this.ProcessMode || (this.CreateMode && !_.isEmpty(this.Products.List));
	}
	get CanChange () { return this.CreateMode && _.isEmpty(this.Products.List)}

	/**
	 * сбор данных с формы редактирования заявки
	 * @param {string} id название параметра
	 * @param {number} value значение параметра
	 */
	collect (id, value) {
		this[id] = value;
		if (/Template/.test(id)) this.Template.Type = value;
	}
	
	/**
	 * СОздание шаблона для заявки
	 * @param {[object]} products список продуктов по настройкам склада
	 */
	setTemplate (products = []) {
		this.Template = new StockTemplate(products, this.TemplateType)
	}

	/** Начало заполнения заявки после заполнения шаблона */
	startRequest() {
		this.Products = new RequestProducts(this.Template.getData(), this.OperationType)
	}

	/**
	 * Проверка корректности заявки:
	 * - при создании выбран склад;
	 * - при создании выбран шаблон заявки;
	 * - заполнено количество хотя бы одного продукта;
	 * - при обработке у каждого заказываемого продукта выбран поставщик.
	 * @returns {boolean} данные заявки корректны
	 */
	validate () {
		var products = this.Products.getData();

		if (this.CreateMode && (this.StockId < 1 || this.StockId === "")) {
			this.ErrorCode = "STOCK_REQUIRED";
			return false;
		}

		if (this.CreateMode && (this.TemplateType < 1 || this.TemplateType === "")) {
			this.ErrorCode = "REQUEST_TEMPLATE_REQUIRED";
			return false;
		}

		if (_.isEmpty(products)) {
			this.ErrorCode = "COUNT_PRODUCT_REQUERIED";
			return false;
		}

		if (this.ProcessMode && _.some(products, (v) => {
			return _.isUndefined(v.ProviderId) || v.ProviderId < 1 || v.ProviderId === "";
		})) {
			this.ErrorCode = "PROVIDER_REQUIRED";
			return false;
		}

		return true;
	}

	/**
	 * Сбор данных для создания или обработки заявки
	 * @returns {object} параметры соответствующего API-запроса
	 */
	getData() {
		var data = this.ProcessMode
			? { RequestId : this.Id }
			: {
				StockId : this.StockId,
				TemplateType : this.TemplateType,
				...(this.TemplateType === 2 && this.Template
					? { RawIds : this.Template.Checked.join(",") }
					: {})
			};

		_.each(this.Products.getData(), (product, index) => {
			var name = "Products." + index + ".";

			data[name + "ProductId"] = product.ProductId;
			data[name + "Count"] = product.Count;

			if (this.ProcessMode) {
				data[name + "ProviderId"] = product.ProviderId;
				data[name + "StockId"] = this.StockId;
			}
		});

		return data;
	}
}
decorate(Request, {
	StockId: observable,
	TemplateType: observable,
	Products: observable,

	ErrorCode: observable,
	ButtonLoading: observable,
})

export class StockTemplate {
	constructor (products = [], type = 1) {
		this.Type = type
		this.List = products
		this.Checked = []
	}

	get Products () {
		return _.isUndefined(this.List) ? [] : 
			_.map(_.groupBy(this.List, "Type"), (list, type) => {
				var sections = _.groupBy(list, "SectionId");
				return {
					Type: parseInt(type, 10),
					Name : _.find(ProductTypeTitles, { Id : parseInt(type, 10) }).Name,
					Sections: _.map(sections, (products, section) => {
						return {
							SectionId: parseInt(section),
							SectionName: products[0].Section,
							Products: _.map(products, (product) => {
								return { Id : product.ProductId, Name : product.Product }
							})
						}
					})
				};
			});
	}

	get CanStart () {
		return this.Type === 1 || (this.Type === 2 && !_.isEmpty(this.Checked))
	}

	/** Сбор данных с формы
	 * @param {[number]} value Значение параметра
	 * @
	 */
	collect(value) { this.Checked = value; }

	/** Сбор данных с формы шаблона */
	getData () {
		if (_.isEmpty(this.List)) return [];
		return _.compact(_.map(this.List, (product) => {
			if (this.Type === 2 && _.indexOf(this.Checked, product.ProductId) === -1) return null;
			else return {
				ProductId: product.ProductId,
				Product: product.Product,
				SectionId: product.SectionId,
				Section: product.Section,
				Type: product.Type,
				StockCount: product.Count,
				UnitName: product.UnitName
			}
		}));
	}
}
decorate(StockTemplate, {
	Checked : observable,
	Type: observable
})

export class RequestProducts {
	constructor(products = [], operationType) {
		this.List = products ? _.map(products, (v) => { return new RequestProduct(v, operationType) }) : []
		this.Filter = "";
	}

	/**
	 * Сбор данных с формы поиска
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 */
	collect(id, value) { this[id] = value; }

	/** Обновление остатков на складе 
	 * @param {[object]} products список продуктов по настройкам склада
	 */
	setActuals(products = []) {
		_.map(this.List, (product) => {
			var item = _.find(products, { ProductId : product.ProductId });
			if (!_.isUndefined(item)) product.setUpdate(item);
		})
	}

	/** Удаление продукта из заявки по ID продукта */
	remove = (id) => { this.List = _.reject(this.List, { ProductId : id }); }

	/** Фильтрация продуктов по поисковой строке */
	get Filtered () {
		var mask = _.toLower(this.Filter);

		return _.filter(this.List, (product) => {
			return product.SearchName.indexOf(mask) !== -1;
		});
	}

	/** Группировка продуктов по типу и разделам */
	get Types () {
		return _.map(_.groupBy(this.Filtered, "Type"), (list, type) => {
			var pType = _.find(ProductTypeTitles, { Id : parseInt(type) })
			
			return {
				Type: _.isUndefined(type) ? -1 : parseInt(type),
				TypeName : pType ? pType.Name : "",
				ProcessMode: list[0].ProcessMode,
				Sections: _.map(_.groupBy(list, "SectionId"), (products, section) => {
					return {
						SectionId: parseInt(section),
						SectionName: products[0].Section,
						Products: products
					}
				})
			};
		});
	}

	/**
	 * Сбор заполненных продуктов для отправки заявки.
	 * Продукты, вернувшие null, в итоговый список не попадают.
	 * @returns {[object]} список продуктов для API
	 */
	getData() {
		return _.compact(_.map(this.List, (item) => { return item.getData(); }));
	}
}
decorate(RequestProducts, {
	List: observable,
	Filter: observable,
	Filtered: computed,
	Types: computed
})

export class RequestProduct {
	constructor(item, operationType) {
		this.OperationType = operationType;
		this.ProductId = item && item.ProductId ? item.ProductId : -1;
		this.Product = item && item.Product ? item.Product : "";
		this.SearchName = _.toLower(this.Product);
		this.SectionId = item && item.SectionId ? item.SectionId : -1;
		this.Section = item && item.Section ? item.Section : "";
		this.Type = item && item.Type ? item.Type : -1;
		this.UnitName = item && item.UnitName ? item.UnitName : "";
		this.StockCount = item && item.StockCount ? item.StockCount : "";
		this.ProviderId = item && item.ProviderId ? item.ProviderId : -1;
		
		this.Actual = "";
		this.LastUpdate = "";
		
		this.Count = item && !_.isUndefined(item.Count) ? item.Count : "";
		this.Order = item && item.Order ? item.Order
			: this.StockCount > this.Count ? (this.StockCount - this.Count).toFixed(3) : 0

	}

	get CreateMode () { return this.OperationType === "new"; }
	get ProcessMode () { return this.OperationType === "process"; }
	
	/**
	 * Сбор данных с формы продуктов
	 * @param {string} id 
	 * @param {number} value 
	 */
	collect(id, value) { this[id] = value; }

	/**
	 * Обновление дпанных по актуальному состоянию склада
	 * @param {object} item 
	 */
	setUpdate(item = {}) {
		this.Product = item.Product ? item.Product : this.Product;
		this.SearchName = _.toLower(this.Product);
		this.SectionId = item.SectionId ? item.SectionId : this.SectionId;
		this.Section = item.Section ? item.Section : this.Section;
		this.Type = item.Type ? item.Type : this.Type;
		this.UnitName = item.UnitName ? item.UnitName : this.UnitName;

		this.Actual = !_.isUndefined(item.Count) ? item.Count : this.Actual;
		this.LastUpdate = item.LastUpdate ? item.LastUpdate : this.LastUpdate;

		this.Count = this.Count !== "" ? this.Count
			: _.isNumber(this.Actual) ? this.Actual.toFixed(3) : this.Actual;
	}

	/**
	 * Сбор одного продукта для отправки заявки.
	 * При создании пропускается продукт без Count, при обработке — без положительного Order.
	 * В режиме обработки Count содержит значение Order и дополнительно передается ProviderId.
	 * @returns {{ ProductId: number, Count: number } | { ProductId: number, Count: number, ProviderId: number } | null}
	 * продукт для API или null, если продукт не должен отправляться
	 */
	getData() {
		if (this.CreateMode && this.Count === "") return null;
		if (this.ProcessMode && (this.Order === "" || this.Order <= 0)) return null;

		return {
			ProductId : this.ProductId,
			Count : this.ProcessMode ? this.Order : this.Count,
			...(this.ProcessMode ? { ProviderId : this.ProviderId } : {})
		}
	}
}
decorate(RequestProduct, {
	CreateMode: computed,
	ProcessMode: computed,
	ProviderId: observable,
	Count: observable,
	Order: observable,
	Product: observable,
	Actual: observable,
	LastUpdate: observable,
	MinCount: observable,
	UnitName: observable,
	Section: observable,
	SectionId: observable,
	Type: observable
})
